import * as Math2D from "../../utils/lineMath";
import { useLayoutEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import styles from "./selector.module.scss";
import { WORD_DIRECTIONS } from "../../utils/consts";
import clsx from "clsx";

function cssDirection(direction) {
  switch (direction) {
    case WORD_DIRECTIONS.NEUTRAL:
      return styles.neutral;
    case WORD_DIRECTIONS.NORTH:
      return styles.n;
    case WORD_DIRECTIONS.NORTH_EAST:
      return styles.ne;
    case WORD_DIRECTIONS.EAST:
      return styles.e;
    case WORD_DIRECTIONS.SOUTH_EAST:
      return styles.se;
    case WORD_DIRECTIONS.SOUTH:
      return styles.s;
    case WORD_DIRECTIONS.SOUTH_WEST:
      return styles.sw;
    case WORD_DIRECTIONS.WEST:
      return styles.w;
    case WORD_DIRECTIONS.NORTH_WEST:
      return styles.nw;
  }
}

function cssAnimateClasses(state) {
  switch (state) {
    default:
      return styles.default;
    case 1:
      return styles.placedOk;
    case 2:
      return styles.placedWrong;
    case 3:
      return styles.ghost;
  }
}

export default function Selector(props) {
  const { selection, placed, foundWordIndex, shouldAnimate } = props;

  const [length, setLength] = useState(0);
  const [thickness, setThickness] = useState(0);

  const ref = useRef();

  const calculateSelectorDimensions = useCallback(() => {
    if (!selection?.center || !selection?.target) {
      return;
    }

    const d = Math2D.cartesianDistance(selection.center, selection.target);
    
    if (!isFinite(d) || d < 0) {
      return;
    }

    /** @type {HTMLElement} */
    const node = ref.current;
    if (!node) return;
    
    /** @type {HTMLElement} */
    const gridNode = node.parentElement;
    if (!gridNode) return;

    const gridRect = gridNode.getBoundingClientRect();
    const gridSizeProperty = window.getComputedStyle(gridNode).getPropertyValue("--gridSize");
    
    if (!gridRect.width || !gridSizeProperty || gridRect.width <= 0) {
      return;
    }

    const gridSize = parseFloat(gridSizeProperty);
    if (!isFinite(gridSize) || gridSize <= 0) {
      return;
    }

    const diameter = (gridRect.width / gridSize) * 0.66;
    
    if (!isFinite(diameter) || diameter <= 0) {
      return;
    }

    node.style.setProperty("--selectorCircleRadius", `${diameter / 2}px`);

    const calculatedLength = d + diameter;
    const calculatedThickness = diameter;
    
    if (isFinite(calculatedLength) && calculatedLength >= 0) {
      setLength(calculatedLength);
    }
    
    if (isFinite(calculatedThickness) && calculatedThickness >= 0) {
      setThickness(calculatedThickness);
    }
  }, [selection.center, selection.target]);

  useLayoutEffect(() => {
    calculateSelectorDimensions();
  }, [calculateSelectorDimensions]);

  const getFoundWordClass = () => {
    if (placed && selection.state === 1 && foundWordIndex !== undefined) {
      const colorClasses = ['foundWord1', 'foundWord2', 'foundWord3', 'foundWord4', 'foundWord5'];
      const colorIndex = foundWordIndex % 5;
      return styles[colorClasses[colorIndex]];
    }
    return '';
  };

  return (
    <div
      style={{
        position: "absolute",
        left: selection.center.x,
        top: selection.center.y,
        pointerEvents: "none",
      }}
      className={clsx(styles.wrapper, cssDirection(selection.direction))}
      ref={ref}
    >
      <motion.div
        className={clsx(
          styles.divTronik,
          placed && cssAnimateClasses(selection.state),
          getFoundWordClass()
        )}
        style={{
          height: isFinite(thickness) && thickness >= 0 ? thickness : 0,
          borderRadius: isFinite(thickness) && thickness >= 0 ? thickness / 2 : 0,
        }}
        initial={shouldAnimate ? { width: 0 } : { width: isFinite(length) && length >= 0 ? length : 0 }}
        animate={{ width: isFinite(length) && length >= 0 ? length : 0 }}
        transition={shouldAnimate ? { 
          duration: 0.4,
          ease: "easeOut"
        } : { duration: 0 }}
      />
    </div>
  );
}
